// App URL Validation
// ------------------
// Shared front-end validation + canonicalisation for a web-application URL,
// used by the Add-Application wizard (ProjectProperties) and the Settings
// domain editor (ShowSettings). The BACKEND is the sole authority
// (normalize_scan_target_url + normalizeUrl + doesUrlExist); this mirrors those
// rules on the FE only so the user gets an immediate, specific message instead
// of a server round-trip. Keep the ruleset in sync with the backend guards.

import { HTTP, HTTPS } from "@/constants/definitions";
import { addProtocolIfMissing } from "@/utils/transform";
import { normalizeUrl } from "@/utils/normalizeUrl";
import { hasDisallowedUrlScheme } from "@/utils/urlSafety";

// Character whitelist for the host+path portion of an application URL (scheme
// stripped before the test). Mirrors the backend scan-target grammar.
export const APP_URL_WHITELIST_CHARS =
  /^(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)(?:\.(?:[a-z¡-￿0-9]-*)*[a-z¡-￿0-9]+)*(?:\.(?:[a-z¡-￿]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/;

export const ERROR_HTTPS_REQUIRED = "Only HTTPS URLs are accepted";
export const ERROR_INVALID_CHARS_URL = "Invalid URL";
export const APPURL_MUST_BE_SET_ERROR = "The Application URL must be set";

export interface AppUrlValidationResult {
  // The canonical URL to send to the backend, present only when valid.
  canonicalUrl?: string;
  // A user-facing error message, present only when invalid.
  error?: string;
}

// Validate a raw URL input and return its canonical form, or an error message.
// Rejects explicit non-http(s) schemes and explicit http:// (HTTPS-only), then
// canonicalises via normalizeUrl so FE-side comparisons match the backend.
export function validateAndCanonicaliseAppUrl(rawUrl: string): AppUrlValidationResult {
  const trimmedUrl = (rawUrl ?? "").trim();
  if (!trimmedUrl) {
    return { error: APPURL_MUST_BE_SET_ERROR };
  }
  // Reject an explicit non-http(s) scheme (javascript:, data:, …) before
  // addProtocolIfMissing masks it by prepending https://.
  if (hasDisallowedUrlScheme(trimmedUrl)) {
    return { error: ERROR_HTTPS_REQUIRED };
  }
  const normalizedUrl = addProtocolIfMissing(trimmedUrl);
  if (!normalizedUrl) {
    return { error: APPURL_MUST_BE_SET_ERROR };
  }
  // Explicit http:// is not allowed.
  if (normalizedUrl.startsWith(HTTP) && !normalizedUrl.startsWith(HTTPS)) {
    return { error: ERROR_HTTPS_REQUIRED };
  }
  const hostAndPath = normalizedUrl.replace(HTTPS, "");
  if (!APP_URL_WHITELIST_CHARS.test(hostAndPath)) {
    return { error: ERROR_INVALID_CHARS_URL };
  }
  return { canonicalUrl: normalizeUrl(normalizedUrl) };
}
